/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { v4 as uuid } from 'uuid';
import type { LocalViewId, ViewKind } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import { CONTAINER } from './container.constants.tsx';
import type { Props, State } from './NavigationView.types.tsx';
import { withRefreshBlocker } from './withRefreshBlocker.tsx';
import {
	modifyView,
	updateContainerState,
	transformRemoteView,
	createFakeId,
	findView,
	makeNewTitle,
} from './updateContainerState.tsx';

export const createView = (viewType: ViewKind, parentId?: LocalViewId): Action<State, Props> =>
	withRefreshBlocker(
		async (
			{ setState, getState },
			{
				navigationRemote,
				cloudId,
				containerId,
				containerType,
				onFailure,
				onSuccess,
				onViewCreated,
			},
		) => {
			if (
				!cloudId ||
				!containerId ||
				!navigationRemote ||
				getState().containers[containerId]?.isLoading ||
				!getState().containers[containerId]?.initialized
			) {
				return;
			}

			if (viewType === 'SECTION') {
				return;
			}

			experience.navBar.createView.start();

			const viewId = createFakeId();
			const name = makeNewTitle();
			const newView = {
				id: viewId,
				polarisId: 0,
				localId: uuid(),
				name,
				viewType,
				views: undefined,
				isLocked: true,
				isManuallyCreated: true,
			};

			const viewSet = getState().containers[containerId].views.find(
				(view) => view?.localId === parentId,
			);
			if (viewSet) {
				setState(
					updateContainerState(getState(), containerId, {
						views: getState().containers[containerId].views.map((view) =>
							view.localId === parentId
								? {
										...view,
										views: [...(view.views || []), newView],
									}
								: view,
						),
					}),
				);
			} else {
				setState(
					updateContainerState(getState(), containerId, {
						views: [...getState().containers[containerId].views, newView],
					}),
				);
			}

			try {
				const viewsetId = viewSet?.id;
				const parentType =
					containerType === CONTAINER.PROJECT ? CONTAINER.PROJECT : CONTAINER.COLLECTION;
				const remoteView = await navigationRemote.createView({
					viewType,
					name,
					containerId,
					parentId: viewSet ? String(viewsetId) : containerId,
					parentType: viewSet ? CONTAINER.SECTION : parentType,
				});
				setState(
					modifyView(getState(), containerId, (view) =>
						view.localId === newView.localId
							? {
									...transformRemoteView(remoteView),
									localId: newView.localId,
									isManuallyCreated: true,
									isLocked: false,
								}
							: view,
					),
				);
				onSuccess?.('createView');
				const navView = findView(getState().containers[containerId], newView.localId).view;
				if (navView) {
					onViewCreated?.(navView);
				}

				experience.navBar.createView.success();

				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState(
					modifyView(getState(), containerId, (view) =>
						view.localId === newView.localId ? { ...view, isLocked: false } : view,
					),
				);

				onFailure?.(error, 'createView');

				if (isClientFetchError(error)) {
					experience.navBar.createView.abort(error);
					return;
				}

				experience.navBar.createView.failure(error);
			}
		},
	);
